import { useRef, useEffect, useState } from 'react';
import { useSnapCarousel } from 'react-snap-carousel';
import { Box, IconButton, LinearProgress, Stack, Link } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router';
import BrandHeading from '../../Page/BrandHeading';
import { t } from 'i18next';
import { apiStates, CONTENT_TYPE } from '../../../types/types';
import { useArticles, UseArticlesResult } from '../../../hooks/useArticles';
import ErrorView from '../../ErrorViews/ErrorView';
import { JaaRoute } from '../../../utilities/Links';
import { LastCard, NewsCard } from './NewsCarouselCards';
import { NewsSkeletons } from './NewsSkeletons';

const ARTICLE_LIMIT = 14;

export default function NewsCarousel() {
  const { articles, apiError, state }: UseArticlesResult = useArticles({
    limit: ARTICLE_LIMIT,
    contentType: [CONTENT_TYPE.LegalNews],
  });
  const {
    scrollRef,
    pages,
    activePageIndex,
    prev,
    next,
    hasNextPage,
    hasPrevPage,
  } = useSnapCarousel();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      scrollRef(containerRef.current);
    }
  }, [scrollRef]);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
        const scrollPercentage =
          (scrollLeft / (scrollWidth - clientWidth)) * 100;
        setScrollProgress(scrollPercentage);
      }
    };

    const currentRef = containerRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
      return () => currentRef.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const renderContent = () => {
    if (state === apiStates.FETCHING) {
      return NewsSkeletons(ARTICLE_LIMIT + 1); // +1 for the last "card" that is a link to all news
    }

    if (apiError) {
      return (
        <Box
          sx={{
            paddingX: { xs: 2, sm: 0 },
          }}
        >
          <ErrorView apiError={apiError} />
        </Box>
      );
    }

    if (!articles || articles.length === 0) {
      return null;
    }

    return (
      <>
        {articles.map((article, index) => (
          <NewsCard key={article.id} article={article} index={index} />
        ))}
        <LastCard />
      </>
    );
  };

  return (
    <Box>
      <Stack
        direction='row'
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Link
          component={RouterLink}
          to={JaaRoute.newsIndex}
          underline='none'
          sx={{ textDecoration: 'none' }}
        >
          {' '}
          <BrandHeading variant='h2' color={'text.primary'}>
            {t('frontPage.legalNews')}
          </BrandHeading>
        </Link>
        <Box
          sx={{
            padding: 1,
          }}
        >
          <IconButton
            onClick={() => prev()}
            disabled={!hasPrevPage}
            aria-disabled={!hasPrevPage}
            aria-label='Previous news item'
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => next()}
            disabled={!hasNextPage}
            aria-disabled={!hasNextPage}
            aria-label='Next news item'
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          WebkitMaskImage:
            'linear-gradient(to left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1) 48px)',
          maskImage:
            'linear-gradient(to left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1) 48px)',
        }}
      >
        <Stack
          ref={containerRef}
          direction='row'
          useFlexGap
          sx={{
            gap: 6,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {renderContent()}
        </Stack>
      </Box>
      <LinearProgress
        variant='determinate'
        value={scrollProgress}
        sx={{
          marginY: 2,
          height: '1px',
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        }}
        aria-label={`${t('articles.title')} ${activePageIndex + 1}-${pages.length} ${t('articles.outOfTotalCount', { count: pages.length })}`}
      />
    </Box>
  );
}
